import React, { useState, useEffect, useCallback, useMemo } from 'react';
import {
  Typography, Button, Tag, Descriptions, Spin, Card, Form, Input, Space, Divider, Select, message,
} from 'antd';
import { ArrowLeftOutlined, SaveOutlined } from '@ant-design/icons';
import { useParams, useNavigate } from 'react-router-dom';
import { formatTS } from '../utils/time';
import api from '../api';
import LinkifiedText from '../components/LinkifiedText';
import ScheduleVarianceFields, {
  toVarianceBody, fromVarianceRow, VARIANCE_STATUS_COLOR, VARIANCE_STATUS_OPTIONS,
} from '../components/ScheduleVarianceFields';
import { useBackFallback, usePageLabel } from '../components/BackChip';

export default function ScheduleVariancePage() {
  const { id } = useParams();
  // The Back chip (Layout): back where you came from, else this list.
  useBackFallback('/schedule-variances', 'Schedule Variance Log');
  usePageLabel(`Schedule Variance #${id}`);
  const navigate = useNavigate();
  const [form] = Form.useForm();

  const [variance, setVariance] = useState(null);
  const [canEdit, setCanEdit]   = useState(false);
  const [loading, setLoading]   = useState(true);
  const [saving, setSaving]     = useState(false);
  const [comments, setComments] = useState([]);
  const [commentText, setCommentText]     = useState('');
  const [addingComment, setAddingComment] = useState(false);

  const load = useCallback(async () => {
    setLoading(true);
    try {
      const res = await api.get(`/api/schedule-variances/${id}`);
      setVariance(res.data.variance);
      setCanEdit(!!res.data.can_edit);
      setComments(res.data.comments || []);
    } catch (err) {
      message.error(err.response?.status === 404
        ? 'Schedule variance not found, or you do not have access to it.'
        : 'Failed to load schedule variance.');
      setVariance(null);
    } finally {
      setLoading(false);
    }
  }, [id]);

  useEffect(() => { load(); }, [load]);

  // Set the form once the fields are mounted (they only render when editable).
  useEffect(() => {
    if (variance && canEdit) form.setFieldsValue(fromVarianceRow(variance));
  }, [variance, canEdit, form]);

  const initialEmployee = useMemo(() => (variance ? {
    value:  variance.UserID,
    label:  variance.TeamName ? `${variance.EmployeeName} (${variance.TeamName})` : variance.EmployeeName,
    teamId: variance.TeamID || null,
  } : null), [variance]);

  const handleSave = async () => {
    let values;
    try { values = await form.validateFields(); } catch { return; }
    setSaving(true);
    try {
      await api.put(`/api/schedule-variances/${id}`, toVarianceBody(values));
      message.success('Changes saved.');
      load();
    } catch (err) {
      message.error(err.response?.data?.error || 'Failed to save changes.');
    } finally {
      setSaving(false);
    }
  };

  const handleAddComment = async () => {
    const text = commentText.trim();
    if (!text) return;
    setAddingComment(true);
    try {
      await api.post(`/api/schedule-variances/${id}/comments`, { comment: text });
      setCommentText('');
      const res = await api.get(`/api/schedule-variances/${id}`);
      setComments(res.data.comments || []);
    } catch (err) {
      message.error(err.response?.data?.error || 'Failed to add comment.');
    } finally {
      setAddingComment(false);
    }
  };

  if (loading) {
    return <div style={{ display: 'flex', justifyContent: 'center', marginTop: 80 }}><Spin size="large" /></div>;
  }
  if (!variance) {
    return (
      <Button icon={<ArrowLeftOutlined />} onClick={() => navigate('/schedule-variances')}>
        Back to Schedule Variance Log
      </Button>
    );
  }

  const system = (
    <Descriptions column={{ xs: 1, md: 3 }} size="small" style={{ marginBottom: 16 }}>
      <Descriptions.Item label="Reporter">{variance.ReporterName || '—'}</Descriptions.Item>
      <Descriptions.Item label="Created On">{formatTS(variance.CreatedAt)}</Descriptions.Item>
      <Descriptions.Item label="Changed On">
        {formatTS(variance.UpdatedAt)}{variance.UpdatedByName ? ` by ${variance.UpdatedByName}` : ''}
      </Descriptions.Item>
    </Descriptions>
  );

  // Laid out like the error submission page: title row, then Status and Save
  // side by side. One Form around both so Status saves with the fields.
  return (
    <Form form={form} layout="vertical">
      <div style={{ display: 'flex', alignItems: 'center', gap: 12, flexWrap: 'wrap', marginBottom: 8 }}>
        <Typography.Title level={4} style={{ margin: 0 }}>Schedule Variance #{variance.ID}</Typography.Title>
        <Tag color={VARIANCE_STATUS_COLOR[variance.Status] ?? 'default'} style={{ fontSize: 13 }}>
          Status: {variance.Status}
        </Tag>
        <Typography.Text type="secondary" style={{ fontSize: 13 }}>
          Created: {String(formatTS(variance.CreatedAt, 'YYYY-MM-DD'))}
        </Typography.Text>
      </div>
      {canEdit && (
        <Space wrap style={{ marginBottom: 16 }}>
          <Typography.Text>Status:</Typography.Text>
          <Form.Item name="status" noStyle rules={[{ required: true, message: 'Required' }]}>
            <Select options={VARIANCE_STATUS_OPTIONS} style={{ width: 180 }} />
          </Form.Item>
          <Button type="primary" icon={<SaveOutlined />} onClick={handleSave} loading={saving}>
            Save
          </Button>
        </Space>
      )}

      <Card>
        {system}
        {canEdit ? (
          <ScheduleVarianceFields form={form} initialEmployee={initialEmployee} lockEmployee showStatus={false} />
        ) : (
          <Descriptions column={{ xs: 1, md: 2 }} bordered size="small">
            <Descriptions.Item label="Employee">{variance.EmployeeName}</Descriptions.Item>
            <Descriptions.Item label="Team">{variance.TeamName || '—'}</Descriptions.Item>
            <Descriptions.Item label="Variance Type">{variance.VarianceType}</Descriptions.Item>
            <Descriptions.Item label="Date of Variance">{String(variance.VarianceDate || '').slice(0, 10) || '—'}</Descriptions.Item>
            <Descriptions.Item label="Shift Start (hours affected)">{variance.ShiftStart || '—'}</Descriptions.Item>
            <Descriptions.Item label="Shift End (hours affected)">{variance.ShiftEnd || '—'}</Descriptions.Item>
            <Descriptions.Item label="Billable OT Hours">
              {variance.BillableOTHours == null ? '—' : Number(variance.BillableOTHours).toFixed(2)}
            </Descriptions.Item>
            <Descriptions.Item label="Remove Points">{variance.RemovePoints}</Descriptions.Item>
            <Descriptions.Item label="Status">
              <Tag color={VARIANCE_STATUS_COLOR[variance.Status] ?? 'default'}>{variance.Status}</Tag>
            </Descriptions.Item>
            <Descriptions.Item label="Notes" span={2}>
              {variance.Notes ? <LinkifiedText text={variance.Notes} /> : '—'}
            </Descriptions.Item>
            <Descriptions.Item label="Billing Notes" span={2}>
              {variance.BillingNotes ? <LinkifiedText text={variance.BillingNotes} /> : '—'}
            </Descriptions.Item>
          </Descriptions>
        )}
      </Card>

      <Card style={{ marginTop: 16 }}>
        <Typography.Text strong style={{ fontSize: 15, display: 'block', marginBottom: 12 }}>
          Comments
        </Typography.Text>

        {comments.length === 0 && (
          <Typography.Text type="secondary">No comments yet.</Typography.Text>
        )}

        {comments.map((c) => (
          <div key={c.ID} style={{ marginBottom: 12, paddingBottom: 12, borderBottom: '1px solid #f0f0f0' }}>
            <Space>
              <Typography.Text strong>{c.UserName}</Typography.Text>
              <Typography.Text type="secondary" style={{ fontSize: 12 }}>{formatTS(c.CreatedAt)}</Typography.Text>
            </Space>
            <div style={{ marginTop: 4 }}><LinkifiedText text={c.Comment} /></div>
          </div>
        ))}

        <Divider style={{ margin: '12px 0' }} />

        <Space.Compact style={{ width: '100%' }}>
          <Input
            placeholder="Add a comment…"
            value={commentText}
            onChange={(e) => setCommentText(e.target.value)}
            onPressEnter={handleAddComment}
          />
          <Button
            type="primary"
            onClick={handleAddComment}
            loading={addingComment}
            disabled={!commentText.trim()}
          >
            Add
          </Button>
        </Space.Compact>
      </Card>
    </Form>
  );
}
